import { createIntl, createIntlCache } from 'react-intl';
import {
  format as format_dateFns,
  formatDistanceToNow as formatDistanceToNow_dateFns,
  parseISO,
  differenceInSeconds,
  isPast,
} from 'date-fns';
import { enUS, enGB, enCA, enAU, es } from 'date-fns/locale';

import translations from '../../../../i18n/locales';

const ENGLISH_DEFAULT = 'en';

// Define user's language. Different browsers have the user locale defined
// on different fields on the `navigator` object, so we make sure to account
// for these different by checking all of them
// Additionally, we want to fall back to a language for which translations exist.

const languages = navigator.languages || [navigator.language];

const stripRegionCode = (lang) => lang.toLowerCase().split(/[_-]+/)[0];

const languagesForWhichTranslationsExist = languages.filter((lang) => {
  const langWithoutRegionCode = stripRegionCode(lang);
  return translations[langWithoutRegionCode] || translations[lang];
});

const language = languagesForWhichTranslationsExist[0] || ENGLISH_DEFAULT;

// Split locales with a region code
export const languageWithoutRegionCode = stripRegionCode(language);

export const messages =
  translations[languageWithoutRegionCode] || translations[language];

export const locale = languageWithoutRegionCode;

// Get the appropriate date-fns locale based on the browser's language preferences
const getDateFnsLocale = () => {
  // Get the full locale code (e.g., 'en-US', 'en-GB', etc.)
  const fullLocale = languages[0]?.toLowerCase() || 'en-us';

  // Map locale codes to date-fns locales
  switch (fullLocale) {
    case 'en-us':
      return enUS;
    case 'en-gb':
      return enGB;
    case 'en-ca':
      return enCA;
    case 'en-au':
      return enAU;
    case 'es':
    case 'es-es':
    case 'es-mx':
      return es;
    default:
      return enUS; // Default to US English
  }
};

export const dateFnsLocale = getDateFnsLocale();

const cache = createIntlCache();

export const ccIntl = createIntl({ locale, messages }, cache);

// TODO: Add other formats (formatRelative, etc) as we need them
export const formatDate = (date, dateFormat = 'PP') => {
  return format_dateFns(date, dateFormat, {
    locale: dateFnsLocale,
  });
};

export const formatDistanceToNow = (date, options) => {
  return formatDistanceToNow_dateFns(date, {
    ...options,
    locale: dateFnsLocale,
  });
};

export const getCountAndFormattedDistanceToNow = (date, options) => {
  const formattedDistanceToNow = formatDistanceToNow_dateFns(date, {
    ...options,
    locale: dateFnsLocale,
  });
  // Use regex to match the first group of digits in the string
  const match = formattedDistanceToNow.match(/\d+/);
  const count = match ? parseInt(match[0], 10) : 1;

  return {
    count,
    formattedDistanceToNow,
  };
};

export { parseISO, isPast, differenceInSeconds };

/**
 * Formats a date with day of week included (e.g., "Monday, April 29, 2024")
 * Use this when you want to emphasize the day of the week
 */
export const formatDateWithDayOfWeek = (date) => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format_dateFns(dateObj, 'PPPP', {
      locale: dateFnsLocale,
    });
  } catch (error) {
    return '';
  }
};

/**
 * Formats a date without time (e.g., "April 29, 2024")
 * Use this when time precision isn't important
 */
export const formatDateOnly = (date) => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format_dateFns(dateObj, 'PP', {
      locale: dateFnsLocale,
    });
  } catch (error) {
    return '';
  }
};

/**
 * Formats a date with time in a standard format (e.g., "Apr 29, 2024, 2:30 PM")
 * This is the recommended format for most cases where time is important
 */
export const formatDateWithTime = (date) => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format_dateFns(dateObj, 'PPp', {
      locale: dateFnsLocale,
    });
  } catch (error) {
    return '';
  }
};

/**
 * Formats a date with time in a compact format (e.g., "04/09/2024, 2:30 PM")
 * Use this when space is limited but time precision is still needed
 */
export const formatCompactDateWithTime = (date) => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format_dateFns(dateObj, 'Pp', {
      locale: dateFnsLocale,
    });
  } catch (error) {
    return '';
  }
};

/**
 * Formats time only (e.g., "2:30 PM")
 * Use this when you want to display time without date (i.e. on AssetFeed items)
 */
export const formatTimeOnly = (date) => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format_dateFns(dateObj, 'p', {
      locale: dateFnsLocale,
    });
  } catch (error) {
    return '';
  }
};
